import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";

const rules = [
  {
    icon: "ri-book-open-line",
    title: "10 pages minimum",
    desc: "Every day. Rain or shine. Tired or motivated. The number doesn't change.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "30 consecutive days",
    desc: "Streaks matter. If you miss a day, you start over. That's the deal.",
  },
  {
    icon: "ri-file-text-line",
    title: "Any book counts",
    desc: "Fiction. Non-fiction. Essays. Graphic novels. If you're reading, you're in.",
  },
  {
    icon: "ri-bar-chart-line",
    title: "Track your progress",
    desc: "Log it somewhere — your journal, a habit app, a notebook. Visibility builds accountability.",
  },
];

const testimonials = [
  {
    name: "Sarah M.",
    location: "London",
    quote: "I've tried reading challenges before. This one stuck because the bar is low enough to never skip, and high enough to feel like it matters.",
    books: "11 books in 30 days",
  },
  {
    name: "James K.",
    location: "New York",
    quote: "By day 8 I stopped thinking about it as a challenge. It just became what I do in the morning before work.",
    books: "1 book per week",
  },
  {
    name: "Amara T.",
    location: "Lagos",
    quote: "The community aspect changed everything. Knowing others are doing it at the same time made it feel like a shared practice.",
    books: "6 books completed",
  },
];

const faqs = [
  {
    q: "What if I miss a day?",
    a: "You start over. That's the rule. Not because we're harsh — because the 30-day streak is the point. The restart teaches you to protect it.",
  },
  {
    q: "Does audiobooks count?",
    a: "For this challenge, we ask that you read physically or on an e-reader. Active reading is a different cognitive experience than listening.",
  },
  {
    q: "When does the next round start?",
    a: "Challenges start on the 1st of every month. You can join at any time and your 30 days starts from when you sign up.",
  },
  {
    q: "Is there a community?",
    a: "Yes. Once you sign up, you'll receive a link to a private group where participants share pages, books, and reflections.",
  },
];

export default function Challenge() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formState, setFormState] = useState<"idle" | "submitting" | "success" | "error">("idle");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => {
      body.append(key, value.toString());
    });
    setFormState("submitting");
    try {
      const res = await fetch("https://readdy.ai/api/form/d6so51j4rjv84lqbqhgg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      if (res.ok) {
        localStorage.setItem("challenge_joined", "true");
        setFormState("success");
        form.reset();
      } else {
        setFormState("error");
      }
    } catch {
      setFormState("error");
    }
  };

  return (
    <main className="bg-[#F4F3F0]">
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/164c6c6b10ab84106d8093fd5b5c0b4b.jpg"
            alt="30-Day Challenge"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/20" />
        </div>
        <div className="relative z-10 w-full px-8 lg:px-24 pt-24">
          <div className="max-w-2xl">
            <span className="text-[#6B9E8A] text-xs font-semibold uppercase tracking-[0.2em] mb-5 block">
              The 30-Day Challenge
            </span>
            <h1 className="font-serif text-5xl lg:text-7xl text-white leading-[1.08] mb-6">
              Thousands of<br />readers.<br />
              <em className="not-italic text-white/70">One simple habit.</em>
            </h1>
            <p className="text-white/60 text-base leading-relaxed max-w-md">
              Commit to 30 days. 10 pages a day. No exceptions. Join a growing community of people who've decided to show up every single day.
            </p>
          </div>
        </div>
      </section>

      {/* Stats bar */}
      <section className="bg-[#3A5544] py-10 px-8 lg:px-24">
        <div className="max-w-4xl mx-auto grid grid-cols-3 gap-0 divide-x divide-white/20">
          {[
            { value: "12,400+", label: "Total participants" },
            { value: "68%", label: "Completion rate" },
            { value: "Month 1", label: "Next round start" },
          ].map((s) => (
            <div key={s.label} className="text-center px-8">
              <p className="font-serif text-3xl text-white font-bold mb-1">{s.value}</p>
              <p className="text-white/55 text-xs uppercase tracking-widest">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Rules */}
      <section className="py-24 px-8 lg:px-24 bg-[#F4F3F0]">
        <div className="max-w-6xl mx-auto">
          <span className="text-[#3A5544] text-xs font-semibold uppercase tracking-[0.2em] mb-5 block">The Rules</span>
          <h2 className="font-serif text-4xl text-[#1A1A1A] leading-[1.2] mb-14 max-w-xl">
            Simple. Non-negotiable.<br />That's the point.
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {rules.map((rule) => (
              <div key={rule.title} className="border-t border-[#D8D5D0] pt-8">
                <div className="w-10 h-10 flex items-center justify-center mb-5">
                  <i className={`${rule.icon} text-[#3A5544] text-2xl`} />
                </div>
                <h3 className="font-serif text-lg text-[#1A1A1A] mb-3">{rule.title}</h3>
                <p className="text-[#777] text-sm leading-[1.8]">{rule.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-8 lg:px-24 bg-[#111111]">
        <div className="max-w-6xl mx-auto">
          <span className="text-[#6B9E8A] text-xs font-semibold uppercase tracking-[0.2em] mb-5 block">What readers say</span>
          <h2 className="font-serif text-4xl text-white leading-[1.2] mb-14">
            Real people. Real results.
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-white/5 border border-white/8 rounded-lg p-8">
                <p className="text-white/70 text-sm leading-[1.9] mb-8 italic">"{t.quote}"</p>
                <div className="border-t border-white/10 pt-5 flex items-center justify-between">
                  <div>
                    <p className="text-white text-sm font-medium">{t.name}</p>
                    <p className="text-white/35 text-xs mt-0.5">{t.location}</p>
                  </div>
                  <span className="text-[#6B9E8A] text-xs font-medium">{t.books}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Signup Form */}
      <section className="py-28 px-8 lg:px-24 bg-[#F4F3F0]">
        <div className="max-w-xl mx-auto">
          <span className="text-[#3A5544] text-xs font-semibold uppercase tracking-[0.2em] mb-5 block text-center">Become who you already are</span>
          <h2 className="font-serif text-4xl text-[#1A1A1A] text-center leading-[1.2] mb-4">
            You're a reader.<br />This makes it official.
          </h2>
          <p className="text-[#777] text-sm text-center leading-relaxed mb-12">
            This isn't about hitting a number or protecting a streak. It's about making one decision — that you're the kind of person who shows up for themselves every day. The 30 days just makes that real.
          </p>

          {formState === "success" ? (
            <div className="bg-[#3A5544] rounded-lg p-10 text-center">
              <div className="w-12 h-12 flex items-center justify-center mx-auto mb-5">
                <i className="ri-checkbox-circle-line text-white text-3xl" />
              </div>
              <p className="font-serif text-2xl text-white mb-2">You've decided.</p>
              <p className="text-white/60 text-sm leading-relaxed mb-7">That's the hardest part — and you already did it. Check your inbox for your welcome email. Day one starts with you.</p>
              <Link
                to="/tracker"
                className="inline-flex items-center gap-2 bg-white text-[#3A5544] px-6 py-3 rounded text-sm font-medium hover:bg-white/90 transition-colors cursor-pointer whitespace-nowrap"
              >
                Open my 30-day tracker <i className="ri-arrow-right-line" />
              </Link>
            </div>
          ) : (
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-lg p-8 flex flex-col gap-5"
            >
              <div className="flex flex-col gap-1.5">
                <label className="text-[#1A1A1A] text-xs font-semibold uppercase tracking-widest">First Name</label>
                <input
                  name="first_name"
                  type="text"
                  required
                  placeholder="Your first name"
                  className="bg-[#F4F3F0] text-[#1A1A1A] text-sm placeholder-[#BBB] rounded px-4 py-3 outline-none focus:ring-1 focus:ring-[#3A5544] transition"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[#1A1A1A] text-xs font-semibold uppercase tracking-widest">Email</label>
                <input
                  name="email"
                  type="email"
                  required
                  placeholder="your@email.com"
                  className="bg-[#F4F3F0] text-[#1A1A1A] text-sm placeholder-[#BBB] rounded px-4 py-3 outline-none focus:ring-1 focus:ring-[#3A5544] transition"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[#1A1A1A] text-xs font-semibold uppercase tracking-widest">Current Reading Goal</label>
                <select
                  name="reading_goal"
                  className="bg-[#F4F3F0] text-[#555] text-sm rounded px-4 py-3 outline-none focus:ring-1 focus:ring-[#3A5544] transition cursor-pointer"
                >
                  <option>Just building the habit</option>
                  <option>Read 10–20 books this year</option>
                  <option>Read 20–40 books this year</option>
                  <option>Read 40+ books this year</option>
                </select>
              </div>
              {formState === "error" && (
                <p className="text-red-500 text-xs text-center">Something went wrong. Please try again.</p>
              )}
              <button
                type="submit"
                disabled={formState === "submitting"}
                className="bg-[#3A5544] text-white text-sm font-medium py-3.5 rounded hover:bg-[#2D4238] transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {formState === "submitting" ? "Joining..." : "I'm showing up — starting now"}
              </button>
              <p className="text-[#AAA] text-xs text-center leading-relaxed">
                One welcome email. No noise. Just you and the page.
              </p>
              <div className="border-t border-[#F0EEEB] pt-4 text-center">
                <Link to="/tracker" className="text-[#3A5544] text-xs font-medium hover:underline cursor-pointer">
                  Already joined? Open your tracker →
                </Link>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-8 lg:px-24 bg-white">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-serif text-3xl text-[#1A1A1A] mb-10">Common questions</h2>
          <div className="flex flex-col divide-y divide-[#F0EEEB]">
            {faqs.map((faq, i) => (
              <div key={i} className="py-5">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-start justify-between gap-4 text-left cursor-pointer group"
                >
                  <span className="font-serif text-base text-[#1A1A1A] group-hover:text-[#3A5544] transition-colors">{faq.q}</span>
                  <i className={`ri-${openFaq === i ? "subtract" : "add"}-line text-[#999] text-base mt-0.5 flex-shrink-0`} />
                </button>
                {openFaq === i && (
                  <p className="text-[#666] text-sm leading-[1.9] mt-3 pr-8">{faq.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
